import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import RevealLine from "@/components/RevealLine";
import Reveal from "@/components/Reveal";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/i18n";

const INTERESTS = [
  { it: "AIR FORCE ONE LIFE — Abitazioni & Uffici", en: "AIR FORCE ONE LIFE — Homes & Offices" },
  { it: "AIR FORCE ONE — Professionale", en: "AIR FORCE ONE — Professional" },
  { it: "AIR FORCE ONE PRO MAX — Grandi Ambienti", en: "AIR FORCE ONE PRO MAX — Large Spaces" },
  { it: "Filtri di ricambio / manutenzione", en: "Replacement filters / maintenance" },
  { it: "Audit / consulenza tecnica", en: "Audit / technical consulting" },
  { it: "Partnership Carbonplai", en: "Carbonplai partnership" },
  { it: "Altro", en: "Other" },
];

const BUDGETS = [
  { it: "< 25.000 €", en: "< € 25,000" },
  { it: "25.000 — 100.000 €", en: "€ 25,000 — 100,000" },
  { it: "100.000 — 500.000 €", en: "€ 100,000 — 500,000" },
  { it: "> 500.000 €", en: "> € 500,000" },
  { it: "Da definire", en: "To be defined" },
];

export default function Contact() {
  const { tr } = useLang();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    interest: INTERESTS[0].it,
    budget_range: BUDGETS[4].it,
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/quotes", form);
      setSent(true);
      toast.success(tr({ it: "Richiesta inviata. Ti ricontattiamo entro 24h.", en: "Request sent. We will get back to you within 24h." }));
    } catch (err) {
      toast.error(apiError(err) ?? tr({ it: "Errore nell'invio.", en: "Submission error." }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="page-contact" className="bg-white text-[#0a0a0a] pt-32 pb-24">
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-8">
          {tr({ it: "— CONTATTI / PREVENTIVO", en: "— CONTACT / QUOTE" })}
        </div>
        <h1 className="font-display text-[clamp(3rem,10vw,10rem)] leading-[0.86] tracking-tighter">
          <div><RevealLine delay={0.2}>{tr({ it: "Progettiamo", en: "Let's design" })}</RevealLine></div>
          <div>
            <RevealLine delay={0.35} className="font-editorial italic font-normal text-[#3f3f46]">
              {tr({ it: "insieme.", en: "together." })}
            </RevealLine>
          </div>
        </h1>

        <div className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <Reveal className="lg:col-span-4 space-y-10">
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-4">
                {tr({ it: "— SCRIVICI", en: "— WRITE TO US" })}
              </div>
              <a
                href="mailto:info@aerastechnology.it"
                className="font-display text-2xl md:text-3xl tracking-tight aero-link block"
                data-testid="contact-email"
              >
                info@aerastechnology.it
              </a>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-4">
                {tr({ it: "— SEDE", en: "— HEADQUARTERS" })}
              </div>
              <p className="font-body text-[#3f3f46] leading-relaxed">
                Aéràs Technology<br />
                Via del Tiro a Segno 10<br />
                10064 Pinerolo (TO) — Italia<br />
                <a href="tel:+390121097089" className="aero-link text-[#0a0a0a]" data-testid="contact-phone-link">
                  Tel 0121.09.70.89
                </a>
              </p>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-4">
                {tr({ it: "— TEMPI DI RISPOSTA", en: "— RESPONSE TIME" })}
              </div>
              <p className="font-body text-[#3f3f46] leading-relaxed">
                {tr({
                  it: "Rispondiamo entro 24 ore lavorative. Per progetti oltre 100.000 € organizziamo un sopralluogo tecnico gratuito.",
                  en: "We respond within 24 working hours. For projects above € 100,000 we arrange a free technical survey.",
                })}
              </p>
            </div>
            <div className="border border-[#e4e4e7] p-6 bg-[#f4f4f5]">
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-3">
                {tr({ it: "● GARANZIA", en: "● OUR PROMISE" })}
              </div>
              <p className="font-body text-sm text-[#3f3f46] leading-relaxed">
                {tr({
                  it: "Nessuna richiesta viene ignorata. Se non è in linea con la nostra offerta, ti indirizziamo verso un partner tecnico competente.",
                  en: "No request goes unanswered. If it does not match our offering, we point you to a competent technical partner.",
                })}
              </p>
            </div>
          </Reveal>

          <div className="lg:col-span-8">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="border border-[#007A8A] p-12 md:p-16 bg-[#f4f4f5]"
                data-testid="contact-sent"
              >
                <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-4">
                  {tr({ it: "● RICEVUTA", en: "● RECEIVED" })}
                </div>
                <h3 className="font-display text-4xl md:text-5xl tracking-tighter mb-6">
                  {tr({ it: "Grazie,", en: "Thank you," })}{" "}
                  <span className="font-editorial italic font-normal text-[#3f3f46]">
                    {tr({ it: "a presto.", en: "talk soon." })}
                  </span>
                </h3>
                <p className="font-body text-[#3f3f46] max-w-lg">
                  {tr({ it: "Abbiamo ricevuto la tua richiesta. Il nostro team ti ricontatterà a", en: "We received your request. Our team will get back to you at" })}{" "}
                  <span className="text-[#0a0a0a]">{form.email}</span>{" "}
                  {tr({ it: "entro 24 ore lavorative.", en: "within 24 working hours." })}
                </p>
              </motion.div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="border border-[#e4e4e7] bg-[#f4f4f5] p-8 md:p-12 space-y-8"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label={tr({ it: "Nome e cognome", en: "Full name" })} required>
                    <input required value={form.name} onChange={set("name")} data-testid="contact-name" className="input-aeras" />
                  </Field>
                  <Field label={tr({ it: "Azienda", en: "Company" })}>
                    <input value={form.company} onChange={set("company")} data-testid="contact-company" className="input-aeras" />
                  </Field>
                  <Field label="Email" required>
                    <input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email-input" className="input-aeras" />
                  </Field>
                  <Field label={tr({ it: "Telefono", en: "Phone" })}>
                    <input value={form.phone} onChange={set("phone")} data-testid="contact-phone" className="input-aeras" />
                  </Field>
                  <Field label={tr({ it: "Interesse", en: "Interest" })} required>
                    <select value={form.interest} onChange={set("interest")} data-testid="contact-interest" className="input-aeras">
                      {INTERESTS.map((i) => (
                        <option key={i.it} value={i.it} className="bg-[#f4f4f5]">
                          {tr(i)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label={tr({ it: "Budget indicativo", en: "Indicative budget" })}>
                    <select value={form.budget_range} onChange={set("budget_range")} data-testid="contact-budget" className="input-aeras">
                      {BUDGETS.map((b) => (
                        <option key={b.it} value={b.it} className="bg-[#f4f4f5]">{tr(b)}</option>
                      ))}
                    </select>
                  </Field>
                </div>

                <Field label={tr({ it: "Descrivi il progetto", en: "Describe your project" })} required>
                  <textarea required rows={5} value={form.message} onChange={set("message")} data-testid="contact-message" className="input-aeras resize-none" />
                </Field>

                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <p className="font-mono text-[10px] tracking-widest uppercase text-[#52525b] max-w-md">
                    {tr({
                      it: "Inviando accetti la nostra Privacy Policy · Dati trattati ai sensi del GDPR 2016/679.",
                      en: "By sending you accept our Privacy Policy · Data processed under GDPR 2016/679.",
                    })}
                  </p>
                  <button type="submit" disabled={busy} data-testid="contact-submit" className="btn-primary disabled:opacity-50">
                    {busy ? tr({ it: "Invio…", en: "Sending…" }) : tr({ it: "Invia richiesta →", en: "Send request →" })}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      <style>{`
        .input-aeras {
          width: 100%;
          background: transparent;
          border: none;
          border-bottom: 1px solid #d4d4d8;
          padding: 0.85rem 0;
          color: #0a0a0a;
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 1rem;
          outline: none;
          transition: border-color 0.3s ease;
        }
        .input-aeras:focus { border-bottom-color: #007A8A; }
        .input-aeras::placeholder { color: #71717a; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#3f3f46] mb-2 inline-block">
        {label}
        {required && <span className="text-[#FF4D00] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
